import React, { useState, useEffect, useCallback } from 'react';
import {
  UnauthorizedError,
  getSbaChecklist,
  getSbaUploads,
  uploadSbaDocument,
  downloadSbaPackage,
} from '../services/api';

/**
 * SbaPackagePanel — F4 SBA loan-package assembly + uploads.
 *
 * Shown once bp_status === 'complete'. Renders the pre-submission checklist
 * (forms/schedules/uploads with status chips), an upload control for each
 * passthrough document (PDF only), and the "Download SBA Package" button which
 * streams the assembled ZIP (with a spinner — assembly takes a few seconds).
 *
 * File size is shown for files uploaded in this browser session (the server
 * manifest stores the filename but not the size); on reload only the filename
 * is available.
 */

function fmtSize(bytes) {
  if (bytes == null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function slotKey(uploadType, principalIndex) {
  return `${uploadType}:${principalIndex || 0}`;
}

export default function SbaPackagePanel({
  sessionId,
  refreshSignal,
  title = 'SBA Package',
  open = true,
  onClose = () => {},
  onBack = null,
  embedded = false,
}) {
  const [checklist, setChecklist] = useState(null);
  const [manifest, setManifest] = useState({});       // key → { filename }
  const [sessionFiles, setSessionFiles] = useState({}); // key → { name, size } (this session)
  const [uploadingKey, setUploadingKey] = useState(null);
  const [slotError, setSlotError] = useState({});      // key → message
  const [zipping, setZipping] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [cl, up] = await Promise.all([
        getSbaChecklist(sessionId),
        getSbaUploads(sessionId),
      ]);
      setChecklist(cl);
      const m = {};
      for (const u of up.uploads || []) {
        m[slotKey(u.document_type, u.principal_index)] = {
          filename: u.original_filename,
        };
      }
      setManifest(m);
    } catch (err) {
      if (!(err instanceof UnauthorizedError)) setError(err.message);
    }
  }, [sessionId]);

  useEffect(() => { refresh(); }, [refresh, refreshSignal]);

  const handleFile = async (item, file) => {
    if (!file) return;
    const key = slotKey(item.upload_type, item.principal_index);
    setUploadingKey(key);
    setSlotError((prev) => ({ ...prev, [key]: null }));
    try {
      await uploadSbaDocument(
        sessionId, item.upload_type, item.principal_index ?? null, file,
      );
      setSessionFiles((prev) => ({ ...prev, [key]: { name: file.name, size: file.size } }));
      await refresh();
    } catch (err) {
      if (!(err instanceof UnauthorizedError)) {
        setSlotError((prev) => ({ ...prev, [key]: err.message }));
      }
    } finally {
      setUploadingKey(null);
    }
  };

  const handleDownloadZip = async () => {
    setZipping(true);
    setError(null);
    try {
      await downloadSbaPackage(sessionId);
    } catch (err) {
      if (!(err instanceof UnauthorizedError)) setError(err.message);
    } finally {
      setZipping(false);
    }
  };

  // Shared right-panel shell so this fills the slot exactly like DataPanel.
  // When `embedded` (inside RightPanelTabs), render just the body.
  const shell = (body) =>
    embedded ? (
      <div className="data-panel-body">{body}</div>
    ) : (
      <aside
        className={`data-panel ${open ? 'data-panel-open' : 'data-panel-closed'}`}
        aria-label={title}
      >
        <header className="data-panel-header">
          <h2>{title}</h2>
          <button
            type="button"
            className="data-panel-close-btn"
            aria-label="Close panel"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="data-panel-body">{body}</div>
      </aside>
    );

  if (!checklist) {
    return shell(
      <>
        <p className="sba-panel-status">Loading checklist…</p>
        {error && <div className="sba-panel-error">{error}</div>}
      </>
    );
  }

  const ready = checklist.ready_to_download;

  return shell(
    <>
      {onBack && (
        <button type="button" className="sba-btn sba-back-btn" onClick={onBack}>
          ← Business Plan
        </button>
      )}
      <p className="sba-panel-status">
        Review the checklist and upload your supporting documents. When you're
        ready, download the complete package as a ZIP.
      </p>

      <ul className="sba-checklist">
        {checklist.items.map((item) => {
          const isUpload = !!item.upload_type;
          const key = isUpload ? slotKey(item.upload_type, item.principal_index) : item.label;
          const sessionFile = isUpload ? sessionFiles[key] : null;
          const manifestFile = isUpload ? manifest[key] : null;
          const fileName = sessionFile?.name || manifestFile?.filename || null;
          const fileSize = sessionFile ? fmtSize(sessionFile.size) : null;
          const busy = uploadingKey === key;

          return (
            <li key={key} className="sba-checklist-item">
              <div className="sba-item-row">
                <span className={`sba-chip sba-chip-${item.status}`}>
                  {item.status === 'complete' ? '✓' : item.status === 'needs_action' ? '!' : '○'}
                </span>
                <span className="sba-item-label">
                  {item.label}
                  {!item.required && <span className="sba-optional"> (if applicable)</span>}
                </span>

                {isUpload && (
                  <label className={`sba-upload-btn ${busy ? 'disabled' : ''}`}>
                    {busy ? 'Uploading…' : fileName ? 'Replace' : 'Upload PDF'}
                    <input
                      type="file"
                      accept="application/pdf"
                      hidden
                      disabled={busy}
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        e.target.value = '';   // allow re-selecting the same file
                        handleFile(item, f);
                      }}
                    />
                  </label>
                )}
              </div>

              {isUpload && fileName && (
                <div className="sba-file-meta">
                  <span className="sba-file-name">{fileName}</span>
                  {fileSize && <span className="sba-file-size">· {fileSize}</span>}
                </div>
              )}
              {isUpload && slotError[key] && (
                <div className="sba-slot-error">{slotError[key]}</div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="sba-download">
        <button
          className="sba-btn sba-btn-primary"
          disabled={!ready || zipping}
          title={!ready ? 'Complete the business plan first' : undefined}
          onClick={handleDownloadZip}
        >
          {zipping && <span className="sba-spinner" aria-hidden="true" />}
          {zipping ? 'Assembling…' : 'Download SBA Package (.zip)'}
        </button>
      </div>

      {error && <div className="sba-panel-error">{error}</div>}
    </>
  );
}
